// useThrottled — trailing-edge throttle with manual flush.
//
// Returns a stable callable plus a flush helper. Calling the throttled function
// invokes the underlying fn at most once per `waitMs` interval. A trailing call
// fires after the interval if calls arrived during the cooldown. flush() bypasses
// the timer and runs the pending call immediately — useful at the end of a drag
// gesture so the final value never gets dropped.

import { useEffect, useMemo, useRef } from "react";

export interface ThrottledFn<Args extends unknown[]> {
  (...args: Args): void;
  flush: () => void;
  cancel: () => void;
}

export function useThrottled<Args extends unknown[]>(
  fn: (...args: Args) => void,
  waitMs: number,
): ThrottledFn<Args> {
  const fnRef = useRef(fn);
  const lastCallRef = useRef<number>(0);
  const pendingArgsRef = useRef<Args | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  return useMemo(() => {
    const invoke = () => {
      if (!pendingArgsRef.current) return;
      const args = pendingArgsRef.current;
      pendingArgsRef.current = null;
      lastCallRef.current = Date.now();
      timerRef.current = null;
      fnRef.current(...args);
    };

    const throttled = ((...args: Args) => {
      const now = Date.now();
      const elapsed = now - lastCallRef.current;
      pendingArgsRef.current = args;
      if (elapsed >= waitMs) {
        invoke();
        return;
      }
      if (timerRef.current === null) {
        timerRef.current = setTimeout(invoke, waitMs - elapsed);
      }
    }) as ThrottledFn<Args>;

    throttled.flush = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      invoke();
    };

    throttled.cancel = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingArgsRef.current = null;
    };

    return throttled;
  }, [waitMs]);
}
